import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { Home, BarChart2, ClipboardList, Wallet, LineChart, HelpCircle, TrendingUp, TrendingDown, Shield, RefreshCw, LogOut, Brain, Lock } from "lucide-react";
import Analytics from "./Analytics";
import MarketIntelligence from "./MarketIntelligence";
import DebtManagement from "./DebtManagement";
import HedgeSolutions from "./HedgeSolutions";
import Investments from "./Investments";
import AITreasuryInsights from "./AITreasuryinsights";
import ReceivablesIntelligence from "./ReceivablesIntelligence";
import DailyEmailPage from "./DailyEmailPage";
import MaturityAlertModal from "./MaturityAlertModal";
import AIInsightsPage from "./AIInsightsPage";
import TrialBanner from "../components/TrialBanner";
import { usePlan } from "../hooks/usePlan";
import ImportFinancingPage from "./ImportFinancingPage";

// ── LICENCE BADGE ─────────────────────────────────────────────
// Add this just above: export default function Dashboard()

// ── LOCKED PAGE ───────────────────────────────────────────────
function LockedPage({ title, plan, onBack }) {
  return (
    <div className="max-w-screen-xl mx-auto px-6 py-16 flex flex-col items-center justify-center text-center gap-6">
      <div className="w-20 h-20 rounded-full bg-white/5 border border-white/10 flex items-center justify-center">
        <span className="text-4xl">🔒</span>
      </div>
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">{title}</h2>
        <p className="text-gray-400 text-sm max-w-md">
          This module requires a <span className={`font-semibold ${plan === "Pro" ? "text-purple-400" : "text-blue-400"}`}>{plan}</span> plan.
          You are currently unsubscribed. Upgrade to unlock full access.
        </p>
      </div>
      <div className="flex gap-3">
        <button onClick={onBack}
          className="px-4 py-2 rounded-lg border border-white/10 text-gray-400 text-sm hover:text-white transition-colors">
          ← Go Back
        </button>
        <a href="mailto:founder@nebulafinance.com?subject=Upgrade Enquiry&body=Hi, I'd like to upgrade my plan."
          className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-semibold transition-colors">
          Contact Us to Upgrade
        </a>
      </div>
    </div>
  );
}

function LicenceBadge() {
  const { plan, isTrialExpired, trialDaysRemaining } = usePlan();

  if (plan === 'pro') return (
    <span className="flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400 border border-purple-500/30">
      ✦ Pro
    </span>
  );

  if (plan === 'core') return (
    <span className="flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400 border border-blue-500/30">
      ⚡ Core
    </span>
  );

  if (plan === 'demo' && isTrialExpired) return (
    <span className="flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 border border-red-500/30">
      🔒 Unsubscribed
    </span>
  );

  if (plan === 'demo') return (
    <span className="flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400 border border-yellow-500/30">
      🕒 Demo · {trialDaysRemaining}d left
    </span>
  );

  return null;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [activeTab,    setActiveTab]    = useState("home");
  const [activePage,   setActivePage]   = useState(null);
  const [profile,      setProfile]      = useState(null);
  const [companyId,    setCompanyId]    = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [lastUpdated,  setLastUpdated]  = useState(new Date());
  const [currentUser,  setCurrentUser]  = useState(null);
  const [userRole,     setUserRole]     = useState("viewer"); // "editor" or "viewer"
  const [helpSubject,  setHelpSubject]  = useState("");
const [helpMessage,  setHelpMessage]  = useState("");
const [helpSending,  setHelpSending]  = useState(false);
const [helpSent,     setHelpSent]     = useState(false);

  // ── NEW: separate state set early so loading screen shows real name ──
  const [loadingCompany, setLoadingCompany] = useState("");
  const [loadingUser,    setLoadingUser]    = useState("");
  const [hedgeInitialBox, setHedgeInitialBox] = useState(null);
  const [hedgePreFillRef,  setHedgePreFillRef]  = useState(null);
  const [lcRecords, setLcRecords] = useState([]);
const [bcRecords, setBcRecords] = useState([]);

  // Data states
  const [hedges,       setHedges]       = useState([]);
  const [receivables,  setReceivables]  = useState([]);
  const [payables,     setPayables]     = useState([]);
  const [liveRates, setLiveRates] = useState({usdinr: null, eurinr: null, gbpinr: null});


  // KPI states
  const [fxExposure,   setFxExposure]   = useState(0);
  const [totalHedged,  setTotalHedged]  = useState(0);
  const [invPortfolio, setInvPortfolio] = useState(0);
  const [netLiquidity, setNetLiquidity] = useState(null);

const handleSendHelp = async () => {
  if (!helpSubject.trim() || !helpMessage.trim()) return;
  setHelpSending(true);
  try {
    const { error } = await supabase.from("support_messages").insert({
      company_id: companyId,
      user_name:  currentUser?.name  || "Unknown",
      user_email: currentUser?.email || "Unknown",
      subject:    helpSubject.trim(),
      message:    helpMessage.trim(),
      status:     "unread",
    });
   
    if (error) throw error;
    setHelpSent(true);
    setHelpSubject("");
    setHelpMessage("");
    setTimeout(() => setHelpSent(false), 4000);
  } catch (e) {
    console.error("Help send error:", e);
  } finally {
    setHelpSending(false);
  }
};

  useEffect(() => { fetchUserAndProfile(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [minLoadDone, setMinLoadDone] = useState(false);

useEffect(() => {
  const timer = setTimeout(() => setMinLoadDone(true), 5000);
  return () => clearTimeout(timer);
}, []);

  const isReadOnly = userRole === "viewer";
  const { canAccess, plan, isTrialExpired, trialDaysRemaining } = usePlan();


  // ══════════════════════════════════════════════════════════════
  // FETCH USER, COMPANY, AND PROFILE DATA
  // ══════════════════════════════════════════════════════════════
  const fetchUserAndProfile = async () => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();

    if (userError || !user) {
      navigate("/login");
      return;
    }

    // ── Admin redirect ────────────────────────────────────────
   if (user.email === process.env.REACT_APP_ADMIN_EMAIL) {
  sessionStorage.setItem("nebula_admin", "true");
  // Use navigate instead of window.location.href to preserve session
  navigate("/admin");
  return;
}
    // ── Approval check ────────────────────────────────────────
  const { data: registrations } = await supabase
  .from("user_registrations")
  .select("status")
  .eq("user_id", user.id)
  .order("created_at", { ascending: false })
  .limit(1);

const registration = registrations?.[0] || null;
    if (registration && registration.status !== "approved") {
      navigate("/pending-approval", { state: { email: user.email } });
      return;
    }

     // ── Set real name + company on loading screen early ─────
      if (registration?.full_name)    setLoadingUser(registration.full_name);
      if (registration?.company_name) setLoadingCompany(registration.company_name);

    // ── Access check (revoked / expired) ─────────────────────
    const { data: compUser } = await supabase
      .from("company_users")
      .select("company_id, role, access_expires_at, revoked_at, status")
      .eq("user_id", user.id)
      .maybeSingle();

    if (compUser?.revoked_at || compUser?.status === "revoked") {
      await supabase.auth.signOut();
      navigate("/login");
      return;
    }

    if (compUser?.access_expires_at && new Date(compUser.access_expires_at) < new Date()) {
      await supabase.auth.signOut();
      navigate("/login");
      return;
    }

    if (!compUser?.company_id) {
      navigate("/onboarding");
      return;
    }

    const userCompanyId = compUser.company_id;
    setCompanyId(userCompanyId);

    // ── Set role ──────────────────────────────────────────────
    const userRoleValue = compUser.role || "viewer";
    setUserRole(userRoleValue);

  // ── Fetch all company data ────────────────────────────────
const [profileRes, debtRes, invRes, recRes, payRes, hedgeRes, cashRes, lcRes, bcRes, fxRes] = await Promise.all([
  supabase.from("company_profiles").select("*").eq("company_id", userCompanyId).single(),
  supabase.from("debt_positions").select("*").eq("company_id", userCompanyId).order("created_at", { ascending: false }),
  supabase.from("investments").select("*").eq("company_id", userCompanyId).order("created_at", { ascending: false }),
  supabase.from("receivables").select("*").eq("company_id", userCompanyId).order("created_at", { ascending: false }),
  supabase.from("payables").select("*").eq("company_id", userCompanyId).order("created_at", { ascending: false }),
  supabase.from("hedge_positions").select("*").eq("company_id", userCompanyId).order("created_at", { ascending: false }),
  supabase.from("cash_balances").select("*").eq("company_id", userCompanyId).order("created_at", { ascending: false }),
  supabase.from("import_financing").select("*").eq("company_id", userCompanyId).order("maturity_date", { ascending: true }),
  supabase.from("import_financing_bc").select("*").eq("company_id", userCompanyId).order("repayment_date", { ascending: true }),
  supabase.from("fx_rates").select("currency_pair, rate"),
]);

// ── FX rate lookup ────────────────────────────────────────
const fxRateMap = {};
(fxRes.data || []).forEach(r => { fxRateMap[r.currency_pair] = parseFloat(r.rate) || 0; });

const getSpot = (currency) => {
  if (!currency || currency === 'INR') return 1;
  return fxRateMap[`${currency.toUpperCase()}INR`] || 0;
};

    // ── Update loading screen with real company name if profile has it ──
      if (profileRes.data?.company_name) {
        setLoadingCompany(profileRes.data.company_name);
      }

    if (profileRes.data) {
      const profileData = profileRes.data;
      const debtPositions = debtRes.data || [];
    profileData.allDebt = debtPositions.map(d => ({
  id:               d.id,
  provider:         d.provider || d.debt_type || "Loan",
  loanAmount:       d.loan_amount?.toString(),
  outstanding:      d.outstanding_balance?.toString(),
  interestRate:     d.interest_rate?.toString(),
  market_benchmark: d.market_benchmark?.toString(),
  debt_type:        d.debt_type,
  currency:         d.currency,
  maturity_date:    d.maturity_date,
  start_date:       d.start_date,
  payment_frequency: d.payment_frequency,
  rating:           d.rating,
  rating_provider:  d.rating_provider,
}));
      setProfile(profileData);
      
    }

    const parseAmt = (v) => parseFloat((v || "").toString().replace(/[^0-9.]/g, "")) || 0;

    setHedges(hedgeRes.data || []);
    setReceivables(recRes.data || []);
    setPayables(payRes.data || []);

    const recData   = recRes.data   || [];
    const payData   = payRes.data   || [];
    const hedgeData = hedgeRes.data || [];
    const cashData  = cashRes.data  || [];

    const totalRec = recData.filter(r => r.status !== "Settled").reduce((s, r) => s + parseAmt(r.amount) - (parseFloat(r.settled_amount) || 0), 0);
const totalPay = payData.filter(p => p.status !== "Settled").reduce((s, p) => s + parseAmt(p.amount) - (parseFloat(p.settled_amount) || 0), 0);

const lcData = lcRes.data || [];
const bcData = bcRes.data || [];
setLcRecords(lcData);
setBcRecords(bcData);

const totalLC = lcData
  .filter(r => r.status !== 'Settled' && r.status !== 'Converted to BC')
  .reduce((s, r) => s + (parseFloat(r.amount_fcy) || 0), 0);
const totalBC = bcData
  .filter(r => r.status !== 'Settled')
  .reduce((s, r) => s + (parseFloat(r.amount_fcy) || 0), 0);

setFxExposure(totalRec + totalPay + totalLC + totalBC);

    const hedged = hedgeData
  .filter(h => !['Settled', 'Cancelled'].includes(h.status || 'Active'))
  .reduce((s, h) => s + (parseFloat(h.notional_amount_num) || 0), 0);

const lcHedged = (lcRes.data || [])
  .filter(r => r.status !== 'Settled' && r.status !== 'Converted to BC' && r.hedge_status !== 'Unhedged')
  .reduce((s, r) => s + (parseFloat(r.hedged_amount_fcy) || 0), 0);

const bcHedged = (bcRes.data || [])
  .filter(r => r.status !== 'Settled' && r.hedge_status !== 'Unhedged')
  .reduce((s, r) => s + (parseFloat(r.hedged_amount_fcy) || 0), 0);

setTotalHedged(hedged + lcHedged + bcHedged);

    const investments = invRes.data || [];
    const inv = investments.filter(r => r.status !== "Redeemed").reduce((s, r) => s + parseAmt(r.amount), 0);
    setInvPortfolio(inv);

    const cash = cashData.reduce((s, r) => s + parseAmt(r.current_balance), 0);

   // Net Liquidity — 7 day horizon, all converted to INR
const today = new Date(); today.setHours(0,0,0,0);
const in7   = new Date(today); in7.setDate(today.getDate() + 7);

const isWithin7 = (dateStr) => {
  if (!dateStr) return false;
  const [y,m,d] = dateStr.split('-').map(Number);
  const dt = new Date(y, m-1, d);
  return dt >= today && dt <= in7;
};

// Cash in bank (INR equivalent)
const cashINR = cashData.reduce((s, r) => {
  const bal  = parseAmt(r.current_balance);
  const spot = getSpot(r.currency || 'INR');
  return s + (r.currency === 'INR' || !r.currency ? bal : bal * spot);
}, 0);

// Receivables due in 7 days (INR equivalent)
const recDue7 = recData
  .filter(r => r.status !== 'Settled' && isWithin7(r.due_date))
  .reduce((s, r) => {
    const amt  = parseAmt(r.amount) - (parseFloat(r.settled_amount) || 0);
    const spot = getSpot(r.currency || 'INR');
    return s + (r.currency === 'INR' || !r.currency ? amt : amt * spot);
  }, 0);

// Payables due in 7 days (INR equivalent)
const payDue7 = payData
  .filter(p => p.status !== 'Settled' && isWithin7(p.due_date))
  .reduce((s, p) => {
    const amt  = parseAmt(p.amount) - (parseFloat(p.settled_amount) || 0);
    const spot = getSpot(p.currency || 'INR');
    return s + (p.currency === 'INR' || !p.currency ? amt : amt * spot);
  }, 0);

// LC payments due in 7 days (INR equivalent)
const lcDue7 = lcData
  .filter(r => r.status !== 'Settled' && r.status !== 'Converted to BC')
  .filter(r => {
    const effDate = r.lc_type === 'Usance' && r.usance_period_days
      ? (() => { const d = new Date(r.maturity_date + 'T12:00:00'); d.setDate(d.getDate() + parseInt(r.usance_period_days)); return d.toISOString().split('T')[0]; })()
      : r.maturity_date;
    return isWithin7(effDate);
  })
  .reduce((s, r) => s + (parseFloat(r.amount_fcy) || 0) * getSpot(r.currency), 0);

// BC repayments due in 7 days (INR equivalent)
const bcDue7 = bcData
  .filter(r => r.status !== 'Settled' && isWithin7(r.repayment_date))
  .reduce((s, r) => s + (parseFloat(r.amount_fcy) || 0) * getSpot(r.currency), 0);


setNetLiquidity(cashINR + recDue7 - payDue7 - lcDue7 - bcDue7);

setNetLiquidity(cashINR + recDue7 - payDue7 - lcDue7 - bcDue7);
    setLastUpdated(new Date());

    // ── Current user details for header ──────────────────────
    const { data: userReg } = await supabase
      .from("user_registrations")
      .select("full_name, company_name")
      .eq("user_id", user.id)
      .maybeSingle();

    setCurrentUser({
      name:    userReg?.full_name    || user.email?.split("@")[0],
      company: userReg?.company_name || profileRes.data?.company_name,
      email:   user.email,
      role:    userRoleValue,
    });

  } catch (e) {
    console.error("Error fetching data:", e);
  } finally {
    setLoading(false);
  }
};

  // ══════════════════════════════════════════════════════════════
  // CALCULATED VALUES
  // ══════════════════════════════════════════════════════════════
  const totalDebt = profile?.allDebt?.reduce((sum, d) =>
    sum + (parseFloat((d.loanAmount || "").toString().replace(/[^0-9.]/g, "")) || 0), 0) || 0;

  const totalOutstanding = profile?.allDebt?.reduce((sum, d) =>
    sum + (parseFloat((d.outstanding || d.loanAmount || "").toString().replace(/[^0-9.]/g, "")) || 0), 0) || 0;

  const avgInterestRate = (() => {
    if (!profile?.allDebt?.length) return 0;
    const totalOut = profile.allDebt.reduce((sum, d) =>
      sum + (parseFloat((d.outstanding || d.loanAmount || "").toString().replace(/[^0-9.]/g, "")) || 0), 0);
    if (totalOut === 0) return 0;
    const wtd = profile.allDebt.reduce((sum, d) => {
      const rate = parseFloat((d.interestRate || "").toString().replace(/[^0-9.]/g, "")) || 0;
      const out  = parseFloat((d.outstanding || d.loanAmount || "").toString().replace(/[^0-9.]/g, "")) || 0;
      return sum + rate * out;
    }, 0);
    return (wtd / totalOut).toFixed(2);
  })();

  const formatTime = (date) => date.toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/");
  };

 if (loading || !minLoadDone) return (
  <div className="min-h-screen bg-[#0a0b0f] flex items-center justify-center px-6">
    <div style={{
      background: "#12141a", border: "1px solid #2a2d3a",
      borderRadius: 16, padding: "48px 40px",
      maxWidth: 480, width: "100%", textAlign: "center",
    }}>
      {/* Brand */}
      <div style={{
        width: 56, height: 56, borderRadius: 14,
        background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
        display: "flex", alignItems: "center", justifyContent: "center",
        margin: "0 auto 20px", fontSize: 24, fontWeight: 900, color: "#fff",
      }}>N</div>

      
     {/* ── FIX: use loadingUser / loadingCompany which are set early ── */}
        <h1 style={{ fontSize: 22, fontWeight: 800, color: "#f1f5f9", margin: "0 0 6px" }}>
          {loadingUser ? `Welcome, ${loadingUser}` : "Welcome"}
        </h1>
        <p style={{ fontSize: 13, color: "#818cf8", margin: "0 0 20px", fontWeight: 600 }}>
          {loadingCompany || "Loading your workspace…"}
        </p>

      

      {/* Loader */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
        <div style={{
          width: 20, height: 20, border: "2px solid #6366f1",
          borderTopColor: "transparent", borderRadius: "50%",
          animation: "spin 0.8s linear infinite",
        }} />
        <span style={{ fontSize: 12, color: "#64748b" }}>Loading your treasury data…</span>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  </div>
  );

  return (
    <div className="min-h-screen bg-[#0a0b0f]">

      {/* HEADER */}
      <header className="border-b border-[#2a2d3a] bg-[#12141a] sticky top-0 z-50">
        <div className="max-w-screen-xl mx-auto px-6 py-3 flex items-center justify-between">

          {/* Company + User */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl gradient-primary flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
             <div>
              <h1 className="text-lg font-bold text-white leading-tight">
                {profile?.company_name || "Your Company"}
              </h1>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-xs text-indigo-400">
                  {currentUser?.name ? `👤 ${currentUser.name}` : "Powered by Nebula Treasury AI"}
                </p>

                {/* Licence stage badge */}
                <LicenceBadge />

                {isReadOnly && (
                  <span className="flex items-center gap-1 text-xs text-amber-400 bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 rounded-full">
                    <Lock className="w-2.5 h-2.5" /> Read Only
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Last updated */}
          <div className="hidden md:flex items-center gap-2 text-xs text-gray-400">
            <RefreshCw className="w-3 h-3" />
            Last Updated: {formatTime(lastUpdated)}
          </div>

          {/* Nav tabs */}
          <div className="flex items-center gap-2">
            {[
              { id: "home",      icon: Home,         label: "Home"        },
              { id: "analytics", icon: BarChart2,     label: "Analytics"   },
              { id: "details",   icon: ClipboardList, label: "Details"     },
              { id: "help",      icon: HelpCircle,    label: "Help"        },
              { id: "email",     icon: Brain,         label: "Daily Email" },
            ].map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex flex-col items-center gap-0.5 px-3 py-2 rounded-xl transition-all text-xs font-medium ${
                  activeTab === tab.id
                    ? "bg-indigo-600 text-white"
                    : "bg-[#1a1d26] border border-[#2a2d3a] text-gray-400 hover:text-white hover:border-indigo-500/50"
                }`}>
                <tab.icon className="w-4 h-4" />
                <span className="hidden sm:block">{tab.label}</span>
              </button>
            ))}

            {sessionStorage.getItem("nebula_admin") === "true" && (
              <button onClick={() => navigate("/admin")}
                className="flex flex-col items-center gap-0.5 px-3 py-2 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-xs">
                <Shield className="w-4 h-4" />
                <span className="hidden sm:block">Admin</span>
              </button>
            )}

            <button onClick={handleLogout}
              className="flex flex-col items-center gap-0.5 px-3 py-2 rounded-xl bg-[#1a1d26] border border-[#2a2d3a] text-gray-400 hover:text-red-400 hover:border-red-400/50 transition-all text-xs">
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:block">Logout</span>
            </button>
          </div>
        </div>
      </header>
<TrialBanner />
      {/* MATURITY ALERT MODAL — only for editors */}
      {!isReadOnly && (
        <MaturityAlertModal
          hedges={hedges}
          receivables={receivables}
          payables={payables}
          companyId={companyId}
          onDataRefresh={fetchUserAndProfile}
        />
      )}

      {/* ── HOME TAB ──────────────────────────────────────────── */}
      {activeTab === "home" && (
        <main className="max-w-screen-xl mx-auto px-6 py-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
            <div onClick={() => { setActiveTab("analytics"); setActivePage("market"); }} className="cursor-pointer">
              <KpiCard label="Total FX Exposure" value={fxExposure > 0 ? formatKPIPlain(fxExposure) : "N/A"} sub="Receivables + Payables +LC + BC " trend="up" highlight={false} />
            </div>
            <div onClick={() => { setActiveTab("analytics"); setActivePage("hedge"); }} className="cursor-pointer">
              <KpiCard label="Unhedged Exposure %" value={fxExposure > 0 ? `${((fxExposure - totalHedged) / fxExposure * 100).toFixed(1)}%` : "N/A"} sub="vs total FX exposure" trend="down" highlight={true} />
            </div>
            <div onClick={() => { setActiveTab("analytics"); setActivePage("investments"); }} className="cursor-pointer">
              <KpiCard label="Total Investments" value={invPortfolio > 0 ? formatKPI(invPortfolio) : "N/A"} sub="Active portfolio value" trend="up" highlight={false} />
            </div>
            <div onClick={() => { setActiveTab("analytics"); setActivePage("debt"); }} className="cursor-pointer">
              <KpiCard label="Total Debt Outstanding" value={totalOutstanding > 0 ? formatKPIPlain(totalOutstanding) : "N/A"} sub="From debt management" trend="down" highlight={false} />
            </div>
            <div onClick={() => { setActiveTab("analytics"); setActivePage("market"); }} className="cursor-pointer">
             <KpiCard 
  label="Net Liquidity (7d)" 
  value={netLiquidity !== null ? formatKPI(Math.abs(netLiquidity)) : "N/A"} 
  sub={netLiquidity >= 0 ? "Cash + Rec less Pay due 7d" : "Deficit — obligations exceed cash"} 
  trend={netLiquidity >= 0 ? "up" : "down"} 
  highlight={netLiquidity < 0} 
/>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="nebula-card p-6">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <TrendingDown className="w-5 h-5 text-indigo-400" />
                  <h2 className="text-lg font-bold text-white">Company Dashboard</h2>
                </div>
              </div>

              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Debt Portfolio</h3>
                <button onClick={() => { setActiveTab("analytics"); setActivePage("debt"); }} className="text-xs text-indigo-400 hover:text-indigo-300 font-medium">View All →</button>
              </div>
              <div className="grid grid-cols-2 gap-3 mb-6">
                {[
                  { label: "Total Debt",                  value: totalDebt > 0 ? formatKPIPlain(totalDebt) : "N/A",              color: "text-white"      },
                  { label: "Outstanding Debt",            value: totalOutstanding > 0 ? formatKPIPlain(totalOutstanding) : "N/A", color: "text-red-400"    },
                  { label: "Wtd Avg Rate (Outstanding)",  value: avgInterestRate > 0 ? `${avgInterestRate}%` : "N/A",             color: "text-amber-400"  },
                  { label: "Total Facilities",            value: profile?.allDebt?.length > 0 ? profile.allDebt.length : "N/A",   color: "text-indigo-400" },
                ].map((item, i) => (
                  <div key={i} onClick={() => { setActiveTab("analytics"); setActivePage("debt"); }}
                    className="p-3 rounded-xl bg-[#0a0b0f] border border-[#2a2d3a] cursor-pointer hover:border-indigo-500/50 transition-all">
                    <p className="text-xs text-gray-500 mb-1">{item.label}</p>
                    <p className={`text-lg font-bold ${item.color}`}>{item.value}</p>
                  </div>
                ))}
              </div>

              <div className="overflow-x-auto mb-6">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#2a2d3a]">
                      {["Loan Type","Amount","Interest Rate","Benchmark","Status"].map(h => (
                        <th key={h} className="text-left text-xs text-gray-500 uppercase py-2 pr-3 font-medium">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {profile?.allDebt?.length > 0 ? profile.allDebt.map((d, i) => {
                      const rate = parseFloat((d.interestRate || "").toString().replace(/[^0-9.]/g, "")) || 0;
                      const benchmark = d.market_benchmark ? parseFloat(d.market_benchmark.toString().replace(/[^0-9.]/g, "")) : 9.5;
                      const aboveMarket = rate > benchmark;
                      const belowMarket = rate > 0 && rate < benchmark;
                      return (
                        <tr key={i} className="border-b border-[#2a2d3a]/40">
                          <td className="py-3 pr-3 text-gray-300">{d.provider || "Term Loan"}</td>
                          <td className="py-3 pr-3 text-white font-medium">
                            {d.loanAmount ? formatDebt(parseFloat(d.loanAmount.toString().replace(/[^0-9.]/g, "")), d.currency || "INR") : "-"}
                          </td>
                          <td className="py-3 pr-3 text-gray-300">{rate > 0 ? `${rate}%` : "-"}</td>
                          <td className="py-3 pr-3 text-gray-400">{benchmark > 0 ? `${benchmark}%` : "9.5%"}</td>
                          <td className="py-3">
                            {rate > 0 ? (
                              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${
                                aboveMarket ? "bg-amber-500/20 text-amber-400 border-amber-500/30" :
                                belowMarket ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/30" :
                                              "bg-blue-500/20 text-blue-400 border-blue-500/30"
                              }`}>
                                {aboveMarket ? "ABOVE MARKET" : belowMarket ? "BELOW MARKET" : "AT MARKET"}
                              </span>
                            ) : "-"}
                          </td>
                        </tr>
                      );
                    }) : (
                      <tr>
                        <td colSpan={5} className="py-4 text-center text-gray-500 text-sm">
                          No debt data —{" "}
                          {!isReadOnly && (
                            <button onClick={() => { setActiveTab("analytics"); setActivePage("debt"); }} className="text-indigo-400">
                              Add in Debt Management →
                            </button>
                          )}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Investment Portfolio</h3>
                <button onClick={() => { setActiveTab("analytics"); setActivePage("investments"); }} className="text-xs text-indigo-400 hover:text-indigo-300 font-medium">View All →</button>
              </div>
              <InvestmentKPIs companyId={companyId} onNavigate={() => { setActiveTab("analytics"); setActivePage("investments"); }} />
            </div>

            <div className="nebula-card p-6">
              <div className="flex items-center gap-2 mb-5">
                <span className="text-purple-400 text-lg">✦</span>
                <h2 className="text-lg font-bold text-white">Nebula Intelligence</h2>
              </div>
              <AITreasuryInsights
                profile={profile}
                hedges={hedges}
                receivables={receivables}
                payables={payables}
                lcRecords={lcRecords}
  bcRecords={bcRecords}
              />
              <div className="mt-4 pt-4 border-t border-[#2a2d3a] flex items-center justify-between">
                <span className="text-xs text-gray-500">Last analyzed: {formatTime(lastUpdated)}</span>
                <button onClick={() => { setActiveTab("analytics"); setActivePage("market"); }} className="text-xs text-indigo-400 hover:text-indigo-300 font-medium">
                  View All Insights →
                </button>
              </div>
            </div>
          </div>
        </main>
      )}

      {/* ── ANALYTICS TAB ─────────────────────────────────────── */}
      {activeTab === "analytics" && (
       activePage === "market" ? (
  canAccess("marketIntelligence")
    ? <MarketIntelligence onBack={() => setActivePage(null)} companyId={companyId} onRatesUpdate={setLiveRates}/>
    : <LockedPage title="Market Intelligence" plan="Core" onBack={() => setActivePage(null)} />
) :
       activePage === "debt" ? (
  canAccess("debtManagement")
    ? <DebtManagement onBack={() => setActivePage(null)} companyId={companyId} readOnly={isReadOnly} profile={profile} />
    : <LockedPage title="Debt Management" plan="Core" onBack={() => setActivePage(null)} />
) :
        activePage === "hedge" ? (
  canAccess("marketIntelligence") 
    ? <HedgeSolutions onBack={() => setActivePage(null)} companyId={companyId} readOnly={isReadOnly} />
    : <LockedPage title="Hedge Solutions" plan="Core" onBack={() => setActivePage(null)} />
) :
        activePage === "investments" ? (
  canAccess("investments")
    ? <Investments onBack={() => setActivePage(null)} companyId={companyId} readOnly={isReadOnly} />
    : <LockedPage title="Investments" plan="Core" onBack={() => setActivePage(null)} />
) :
       activePage === "receivables-intelligence" ? (
  canAccess("receivablesIntelligence")
    ? <ReceivablesIntelligence onBack={() => setActivePage(null)} companyId={companyId} />
    : <LockedPage title="Receivables Intelligence" plan="Pro" onBack={() => setActivePage(null)} />
) :
 activePage === "ai-insights" ? (
          canAccess("aiInsights")
            ? <AIInsightsPage
                onBack={() => setActivePage(null)}
                profile={profile}
                hedges={hedges}
                receivables={receivables}
                payables={payables}
                companyId={companyId}
                debtPositions={profile?.allDebt || []}
                liveRates={liveRates} 
                lcRecords={lcRecords}
        bcRecords={bcRecords}
                
                />
            : <LockedPage title="AI-Powered Financial Insights" plan="Core" onBack={() => setActivePage(null)} />
        ) :
activePage === "import-financing" ? (
  canAccess("importFinancing")
    ? <ImportFinancingPage onBack={() => setActivePage(null)} companyId={companyId} onNavigateToHedge={(lcRef) => { setHedgeInitialBox('hedge'); setHedgePreFillRef(lcRef || null); setActiveTab('details'); }} />
    : <LockedPage title="Import Financing" plan="Pro" onBack={() => setActivePage(null)} />
) :

                <main className="max-w-screen-xl mx-auto px-6 py-6">
          <h2 className="text-2xl font-bold text-white mb-6">Analytics</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { id: "market",      title: "Real-Time Market Intelligence", sub: "Nebula FX",             color: "from-indigo-500 to-purple-600",  status: "Live", moduleKey: "marketIntelligence"       },
              { id: "ai-insights", title: "AI-Powered Financial Insights", sub: "Intelligence Platform",  color: "from-pink-500 to-rose-600",     status: "Live", moduleKey: "aiInsights"               },
              { id: null,          title: "Treasury Reporting",            sub: "AI-Powered Reports",     color: "from-violet-500 to-indigo-600", status: "Soon", moduleKey: "treasuryReporting"        },
              { id: "hedge",       title: "Hedge Solutions",               sub: "Risk Management",        color: "from-emerald-500 to-teal-600",  status: "Live", moduleKey: "hedges"                   },
              { id: "debt",        title: "Debt Management",               sub: "Business Finance",       color: "from-amber-500 to-orange-600",  status: "Live", moduleKey: "debtManagement"           },
              { id: "investments", title: "Investments",                   sub: "Wealth Management",      color: "from-cyan-500 to-blue-600",     status: "Live", moduleKey: "investments"              },
              { id: "receivables-intelligence", title: "Receivables Intelligence", sub: "Collections Analytics", color: "from-teal-500 to-cyan-600", status: "Live", moduleKey: "receivablesIntelligence" },
              { id: "import-financing", title: "Import Financing", sub: "LC & Buyers Credit", color: "from-teal-500 to-emerald-600", status: "Live", moduleKey: "importFinancing" },
            ].map((s, i) => {
              const hasAccess    = canAccess(s.moduleKey);
              const requiredPlan = ["receivablesIntelligence", "treasuryReporting"].includes(s.moduleKey) ? "Pro" : "Core";
              return (
                <div key={i}
                  onClick={() => hasAccess && s.id && setActivePage(s.id)}
                  className={`nebula-card p-6 relative ${hasAccess && s.id ? "cursor-pointer" : "cursor-default"}`}>

                  {/* Badge — lock if no access, Live/Soon if access */}
                  {!hasAccess ? (
                    <span className={`absolute top-3 right-3 px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                      requiredPlan === "Pro"
                        ? "bg-purple-500/20 text-purple-400 border-purple-500/30"
                        : "bg-blue-500/20 text-blue-400 border-blue-500/30"
                    }`}>
                      🔒 {requiredPlan}
                    </span>
                  ) : s.status === "Soon" ? (
                    <span className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-gray-700/60 text-[10px] text-gray-400">Coming Soon</span>
                  ) : (
                    <span className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 text-[10px] border border-emerald-500/30">● Live</span>
                  )}

                  {/* Icon */}
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${s.color} flex items-center justify-center mb-4 ${!hasAccess ? "opacity-40" : ""}`}>
                    {s.id === "market"                   ? <TrendingUp className="w-6 h-6 text-white" /> :
                     s.id === "hedge"                    ? <Shield     className="w-6 h-6 text-white" /> :
                     s.id === "debt"                     ? <Wallet     className="w-6 h-6 text-white" /> :
                     s.id === "investments"              ? <LineChart  className="w-6 h-6 text-white" /> :
                     s.title.includes("AI")              ? <Brain      className="w-6 h-6 text-white" /> :
                     s.id === "receivables-intelligence" ? <BarChart2  className="w-6 h-6 text-white" /> :
                     s.id === "import-financing" ? <Wallet className="w-6 h-6 text-white" /> :
                                                           <BarChart2  className="w-6 h-6 text-white" />}
                  </div>

                  {/* Title */}
                  <h3 className={`text-base font-bold mb-1 ${hasAccess ? "text-white" : "text-gray-500"}`}>{s.title}</h3>
                  <p className="text-xs text-indigo-400 mb-3">{s.sub}</p>

                  {/* CTA */}
                  {hasAccess && s.id && (
                    <p className="text-xs text-indigo-400 font-medium">Open →</p>
                  )}
                  {!hasAccess && (
                    <a
                      href={`mailto:founder@nebulafinance.com?subject=Upgrade to ${requiredPlan}&body=Hi Aravind, I'd like to upgrade my Nebula Finance plan.`}
                      onClick={e => e.stopPropagation()}
                      className="text-xs font-medium"
                      style={{ color: requiredPlan === "Pro" ? "#c084fc" : "#818cf8" }}
                    >
                      Upgrade to {requiredPlan} →
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </main>
 )}
      
      {activeTab === "details" && (
  <div>
    {!canAccess("marketIntelligence") && (
      <div className="max-w-screen-xl mx-auto px-6 pt-4">
        <div className="rounded-xl border border-yellow-500/20 bg-yellow-500/5 px-4 py-3 flex items-center gap-3">
          <span className="text-yellow-400 text-sm">💡</span>
          <p className="text-sm text-yellow-200">
            You have access to manage your <span className="font-semibold text-yellow-300">Receivables, Payables, Hedge Positions and Cash in Bank</span> here.
            Upgrade to <span className="font-semibold text-yellow-300">Core or Pro</span> to unlock full analytics.
            <a href="mailto:founder@nebulafinance.com" className="ml-1 underline text-yellow-300 hover:text-white">Contact us →</a>
          </p>
        </div>
      </div>
    )}
    <Analytics companyId={companyId} readOnly={isReadOnly} initialBox={hedgeInitialBox} onClearInitialBox={() => setHedgeInitialBox(null)} preFillRef={hedgePreFillRef}
  preFillType="LC"/>
  </div>
)}

      {/* ── HELP TAB ──────────────────────────────────────────── */}
      {activeTab === "help" && (
        <main className="max-w-screen-xl mx-auto px-6 py-6">
          <h2 className="text-2xl font-bold text-white mb-2">Help & Support</h2>
          <p className="text-gray-400 mb-6 text-sm">Our team is here to help you get the most out of Nebula Finance AI.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="nebula-card p-6">
              <h3 className="text-lg font-bold text-white mb-4">Contact Us</h3>
              <div className="space-y-4">
                {[
                  { label: "Email",          value: "founder@nebulafinance.net",       icon: "✉️" },
                  { label: "Phone",          value: "+91 8590594094",                  icon: "📞" },
                  { label: "WhatsApp",       value: "+91 8590594094",                  icon: "💬" },
                  { label: "Business Hours", value: "Mon–Fri, 9:00 AM – 6:00 PM IST",  icon: "🕐" },
                ].map((c, i) => (
                  <div key={i} className="flex items-center gap-4 p-3 rounded-xl bg-[#0a0b0f] border border-[#2a2d3a]">
                    <span className="text-xl">{c.icon}</span>
                    <div>
                      <p className="text-xs text-gray-500">{c.label}</p>
                      <p className="text-sm text-white font-medium">{c.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="nebula-card p-6">
  <h3 className="text-lg font-bold text-white mb-4">Send a Message</h3>
  {helpSent ? (
    <div className="flex flex-col items-center justify-center py-8 gap-3">
      <div className="w-12 h-12 rounded-full bg-emerald-900/30 border border-emerald-500/30 flex items-center justify-center text-2xl">✓</div>
      <p className="text-emerald-400 font-semibold text-sm">Message sent successfully!</p>
      <p className="text-gray-500 text-xs">We'll get back to you within 24 hours.</p>
    </div>
  ) : (
    <div className="space-y-3">
      <div>
        <label className="block text-xs text-gray-400 mb-1">Subject</label>
        <input
          className="nebula-input text-sm"
          placeholder="How can we help?"
          value={helpSubject}
          onChange={e => setHelpSubject(e.target.value)}
        />
      </div>
      <div>
        <label className="block text-xs text-gray-400 mb-1">Message</label>
        <textarea
          className="nebula-input text-sm h-32 resize-none"
          placeholder="Describe your question or issue..."
          value={helpMessage}
          onChange={e => setHelpMessage(e.target.value)}
        />
      </div>
      <button
        onClick={handleSendHelp}
        disabled={helpSending || !helpSubject.trim() || !helpMessage.trim()}
        className={`nebula-btn-primary w-full text-sm ${
          helpSending || !helpSubject.trim() || !helpMessage.trim()
            ? "opacity-50 cursor-not-allowed" : ""
        }`}
      >
        {helpSending ? "Sending…" : "Send Message"}
      </button>
 <p className="text-xs text-gray-500 text-center">
        Replies sent to {currentUser?.email || "your email"}
      </p>
    </div>
  )}
</div>
          </div>
        </main>
      )}
      {/* ── DAILY EMAIL TAB ───────────────────────────────────── */}
      {activeTab === "email" && (
       <DailyEmailPage
  profile={profile}
  companyId={companyId}
/>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ══════════════════════════════════════════════════════════════
function formatKPI(n) {
  if (!n && n !== 0) return "N/A";
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
  if (n >= 100000)   return `₹${(n / 100000).toFixed(2)}L`;
  if (n >= 1000)     return `₹${(n / 1000).toFixed(2)}K`;
  return `₹${n.toFixed(0)}`;
}

function formatKPIPlain(n) {
  if (!n && n !== 0) return "N/A";
  if (n >= 1000000000) return `${(n / 1000000000).toFixed(2)}B`;
  if (n >= 1000000)    return `${(n / 1000000).toFixed(2)}M`;
  if (n >= 1000)       return `${(n / 1000).toFixed(2)}K`;
  return n.toFixed(0);
}

function formatDebt(n, currency = "INR") {
  if (!n && n !== 0) return "N/A";
  const symbols = { INR: "₹", USD: "$", EUR: "€", GBP: "£", JPY: "¥", AUD: "A$", CAD: "C$", CHF: "CHF " };
  const sym = symbols[currency] || currency + " ";
  if (currency === "INR") {
    if (n >= 10000000) return `${sym}${(n / 10000000).toFixed(2)}Cr`;
    if (n >= 100000)   return `${sym}${(n / 100000).toFixed(2)}L`;
    if (n >= 1000)     return `${sym}${(n / 1000).toFixed(2)}K`;
    return `${sym}${n.toFixed(0)}`;
  }
  if (n >= 1000000000) return `${sym}${(n / 1000000000).toFixed(2)}B`;
  if (n >= 1000000)    return `${sym}${(n / 1000000).toFixed(2)}M`;
  if (n >= 1000)       return `${sym}${(n / 1000).toFixed(2)}K`;
  return `${sym}${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function KpiCard({ label, value, sub, trend, highlight }) {
  return (
    <div className={`rounded-2xl p-4 border ${highlight ? "bg-[#2d1b1b] border-red-500/40" : "bg-[#12141a] border-[#2a2d3a]"}`}>
      <div className="flex items-center gap-1.5 mb-2">
        <span className={`w-2 h-2 rounded-full ${highlight ? "bg-red-400" : "bg-emerald-400"}`} />
        <p className="text-xs text-gray-400 uppercase tracking-wide font-medium">{label}</p>
      </div>
      <p className="text-2xl font-bold text-white mb-1">{value}</p>
      <div className="flex items-center gap-1">
        {trend === "up"
          ? <TrendingUp   className="w-3 h-3 text-emerald-400" />
          : <TrendingDown className="w-3 h-3 text-red-400" />}
        <span className={`text-xs ${trend === "up" ? "text-emerald-400" : "text-red-400"}`}>{sub}</span>
      </div>
    </div>
  );
}

function InvestmentKPIs({ companyId, onNavigate }) {
  const [data,        setData]        = useState(null);
  const [cashBalance, setCashBalance] = useState(0);

  useEffect(() => {
    if (!companyId) return;
    const load = async () => {
      try {
        const [invRes, cashRes] = await Promise.all([
          supabase.from("investments").select("*").eq("company_id", companyId),
          supabase.from("cash_balances").select("*").eq("company_id", companyId),
          supabase.from("fx_rates").select("currency_pair, rate"),
        ]);
        const invData  = invRes.data  || [];
        const cashData = cashRes.data || [];
        const active   = invData.filter(r => r.status !== "Redeemed");
        const total    = active.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
        const wtdReturn = total > 0
          ? active.reduce((s, r) => s + ((parseFloat(r.rate) || 0) * (parseFloat(r.amount) || 0)), 0) / total
          : 0;
        const maturing = active.filter(r => {
          if (!r.end_date) return false;
          const today = new Date(); today.setHours(0, 0, 0, 0);
          const days  = Math.round((new Date(r.end_date) - today) / 86400000);
          return days >= 0 && days <= 30;
        }).length;
        setData({ total, wtdReturn, maturing, count: active.length });
        const totalCash = cashData.reduce((s, r) => s + (parseFloat(r.current_balance?.toString().replace(/[^0-9.]/g, "")) || 0), 0);
        setCashBalance(totalCash);
      } catch (e) { console.error(e); }
    };
    load();
  }, [companyId]);

  const fmt = (n) => {
    if (!n && n !== 0) return "N/A";
    if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
    if (n >= 100000)   return `₹${(n / 100000).toFixed(2)}L`;
    if (n >= 1000)     return `₹${(n / 1000).toFixed(2)}K`;
    return `₹${n.toFixed(0)}`;
  };

  return (
    <div className="grid grid-cols-2 gap-3">
      {[
        { label: "Total Portfolio",  value: data ? fmt(data.total) : "N/A",                 color: "text-white"       },
        { label: "Wtd Avg Return",   value: data ? `${data.wtdReturn.toFixed(2)}%` : "N/A", color: "text-emerald-400" },
        { label: "Cash in Bank",     value: cashBalance > 0 ? fmt(cashBalance) : "N/A",      color: "text-blue-400"    },
        { label: "Maturing in 30d",  value: data ? data.maturing.toString() : "N/A",         color: data?.maturing > 0 ? "text-amber-400" : "text-gray-400" },
      ].map((item, i) => (
        <div key={i} onClick={onNavigate}
          className="p-3 rounded-xl bg-[#0a0b0f] border border-[#2a2d3a] cursor-pointer hover:border-indigo-500/50 transition-all">
          <p className="text-xs text-gray-500 mb-1">{item.label}</p>
          <p className={`text-lg font-bold ${item.color}`}>{item.value}</p>
        </div>
      ))}
    </div>
  );
}
